import {EligibilityFailureScenario, SoleTraderFailure, TollAccountFailure} from './api-services';
import {LinkedAccount} from '../context/ApplicationContext';
import {BaseFeatureFields} from '../hooks/features/useFeaturesQuery';

export interface IReason {
    code: string;
    message: string;
}

interface IStatus {
    code: string; // some examples(but its string so could be more than these) SUCCESS, ERROR, WARNING, APPLICATION_ERROR
    message?: string;
    reasons?: IReason[];
}

export interface IBaseResponse {
    status: IStatus;
}

export type linkedTollAccountsApiErrors = 'userHasOtherMyAccount' | 'generic';

/** Response structure of /eligibility/check endpoint */
export interface EligibilityCheckResponse extends IStatus {
    availableAmount?: number; // Should always be undefined
    claimResult?: 'PASS' | 'FAIL' | 'MANUAL_PASS_UNIFY' | 'OVERRIDDEN' | 'NO_RESULT' | 'MANUAL_ASSESSMENT'; // will usually only be pass or fail
    claims?: EligibilityCheckResponse[];
}

export interface LinkedTollAccountsResponse extends IBaseResponse {
    linkedTollAccounts: LinkedAccount[];
}

/**
 * A list of ALL claim handler response FAILURES (no success ones).
 * Some may overlap with TR2, but we will treat it as the same.
 * This is only meant to be used directly with the api, mapping to a user scenario that we can action.
 */
export enum ApiEligibilityFailure {
    DUPLICATE_CUSTOMER = 'DUPLICATE_CUSTOMER',
    DUPLICATE_TOLL_ACCOUNT = 'DUPLICATE_TOLL_ACCOUNT',

    TOLL_ACCOUNT_ELIGIBILITY_TOLL_ACC_NOT_FOUND = 'TOLL_ACCOUNT_ELIGIBILITY_TOLL_ACC_NOT_FOUND',
    TOLL_ACCOUNT_ELIGIBILITY_TOLL_ACC_NOT_ACTIVE = 'TOLL_ACCOUNT_ELIGIBILITY_TOLL_ACC_NOT_ACTIVE',
    TOLL_ACCOUNT_ELIGIBILITY_TOLL_ACC_NOT_NSW = 'TOLL_ACCOUNT_ELIGIBILITY_TOLL_ACC_NOT_NSW',
    TOLL_ACCOUNT_ELIGIBILITY_TOLL_ACCOUNT_HAS_ABN = 'TOLL_ACCOUNT_ELIGIBILITY_TOLL_ACCOUNT_HAS_ABN',
    TOLL_ACCOUNT_ELIGIBILITY_TOLL_ACCOUNT_HAS_NO_ABN = 'TOLL_ACCOUNT_ELIGIBILITY_TOLL_ACCOUNT_HAS_NO_ABN',
    TOLL_ACCOUNT_ELIGIBILITY_TOLL_ACCOUNT_PRODUCT_TYPE_INVALID = 'TOLL_ACCOUNT_ELIGIBILITY_TOLL_ACCOUNT_PRODUCT_TYPE_INVALID',

    // Not in NSW
    RESIDENTIAL_ADDRESS_WITHIN_STATE = 'RESIDENTIAL_ADDRESS_WITHIN_STATE',

    SERVICE_PROVIDER_ERROR = 'SERVICE_PROVIDER_ERROR',
    ETOLL_SERVICE_PROVIDER_NOT_AVAILABLE = 'ETOLL_SERVICE_PROVIDER_NOT_AVAILABLE', // means circuit breaker is open
    LINKT_SERVICE_PROVIDER_NOT_AVAILABLE = 'LINKT_SERVICE_PROVIDER_NOT_AVAILABLE', // means circuit breaker is open

    FIELD_VALIDATION = 'FIELD_VALIDATION',

    TR2_HAS_MIXED = 'TR2_HAS_MIXED',

    // TR2Only - ABN checks
    TR2_ABN_INVALID_PARAMS = 'TR2_ABN_INVALID_PARAMS',
    TR2_ABN_REQUIRED_ABN = 'TR2_ABN_REQUIRED_ABN',
    TR2_ABN_ABN_ALREADY_LINKED = 'TR2_ABN_ABN_ALREADY_LINKED',
    TR2_ABN_ABR_BUSINESS_ADDRESS_NOT_NSW = 'TR2_ABN_ABR_BUSINESS_ADDRESS_NOT_NSW',
    TR2_ABN_ANZSIC_CODE_NOT_ELIGIBLE = 'TR2_ABN_ANZSIC_CODE_NOT_ELIGIBLE',
    TR2_ABN_ABR_ASSOCIATES_NOT_INCLUDES_APPLICANT = 'TR2_ABN_ABR_ASSOCIATES_NOT_INCLUDES_APPLICANT',
    TR2_ABN_ABN_SUPRESSED = 'TR2_ABN_ABN_SUPRESSED', // spelling error intentional
    TR2_ABN_ABR_ENTITY_NOT_ELIGIBLE = 'TR2_ABN_ABR_ENTITY_NOT_ELIGIBLE',
    TR2_ABN_ABR_NOT_ACTIVE_BEFORE_CURR_FIN_YEAR = 'TR2_ABN_ABR_NOT_ACTIVE_BEFORE_CURR_FIN_YEAR',
    TR2_ABN_INACTIVE_ABN = 'TR2_ABN_INACTIVE_ABN',
    TR2_ABN_ABN_NOT_FOUND = 'TR2_ABN_ABN_NOT_FOUND',

    // TR2Only - Toll account checks
    TR2_TOLL_ACC_INVALID_PARAMS = 'TR2_TOLL_ACC_INVALID_PARAMS',
    TR2_TOLL_ACC_TOLL_ACCOUNT_NUMBER_NOT_VALID = 'TR2_TOLL_ACC_TOLL_ACCOUNT_NUMBER_NOT_VALID',
    TR2_TOLL_ACC_TOLL_ACCOUNT_NUMBER_NOT_ACTIVE = 'TR2_TOLL_ACC_TOLL_ACCOUNT_NUMBER_NOT_ACTIVE',
    TR2_TOLL_ACC_LPN_NOT_ASSOCIATED_WITH_ACC_NO = 'TR2_TOLL_ACC_LPN_NOT_ASSOCIATED_WITH_ACC_NO',
    TR2_TOLL_ACC_LPN_NOMINATED_IS_NULL = 'TR2_TOLL_ACC_LPN_NOMINATED_IS_NULL',
    TR2_TOLL_ACC_TOLL_ACCOUNT_NUMBER_VPN_NOT_EXIST = 'TR2_TOLL_ACC_TOLL_ACCOUNT_NUMBER_VPN_NOT_EXIST',
    TR2_TOLL_ACC_TOLL_PROVIDER_EMAIL_NOT_MATCH = 'TR2_TOLL_ACC_TOLL_PROVIDER_EMAIL_NOT_MATCH',
    TR2_TOLL_ACC_TOLL_ACCOUNT_IS_ASSOCIATED_WITH_ABN = 'TR2_TOLL_ACC_TOLL_ACCOUNT_IS_ASSOCIATED_WITH_ABN',
    TR2_TOLL_ACC_TOLL_ACCOUNT_IS_NOT_ASSOCIATED_WITH_ABN = 'TR2_TOLL_ACC_TOLL_ACCOUNT_IS_NOT_ASSOCIATED_WITH_ABN',
    TR2_TOLL_ACC_TOLL_ACCOUNT_TYPE_INVALID = 'TR2_TOLL_ACC_TOLL_ACCOUNT_TYPE_INVALID',
    TR2_TOLL_ACC_TOLL_ACCOUNT_TYPE_MISSING = 'TR2_TOLL_ACC_TOLL_ACCOUNT_TYPE_MISSING',
    TR2_TOLL_ACC_TOLL_ACCOUNT_NUMBER_ALREADY_LINKED = 'TR2_TOLL_ACC_TOLL_ACCOUNT_NUMBER_ALREADY_LINKED',
    TR2_TOLL_ACC_CUSTOMER_EXCEED_ALLOWED_LINKS = 'TR2_TOLL_ACC_CUSTOMER_EXCEED_ALLOWED_LINKS',
    TR2_TOLL_ACC_INELIGIBLE = 'TR2_TOLL_ACC_INELIGIBLE',
}

/**
 * Api codes that will come from the X handler during submission.
 * May occur during TR2 linking process.
 * Note that these are distinct from codes found in `EligibilityReason` as the
 * code being ran in rebate-txn is different.
 * TR2Only
 */
enum TR2_LINKING_ERROR {
    TR2_SUB_TOLL_ACCOUNT_NUMBER_NOT_VALID = 'TR2_SUB_TOLL_ACCOUNT_NUMBER_NOT_VALID',
    TR2_SUB_LPN_NOT_ASSOCIATED_WITH_ACC_NO = 'TR2_SUB_LPN_NOT_ASSOCIATED_WITH_ACC_NO',
    TR2_SUB_TOLL_ACCOUNT_NUMBER_ALREADY_LINKED = 'TR2_SUB_TOLL_ACCOUNT_NUMBER_ALREADY_LINKED',
    TR2_SUB_CUSTOMER_EXCEED_ALLOWED_LINKS = 'TR2_SUB_CUSTOMER_EXCEED_ALLOWED_LINKS',
    TR2_SUB_TOLL_ACCOUNT_NUMBER_NOT_ACTIVE = 'TR2_SUB_TOLL_ACCOUNT_NUMBER_NOT_ACTIVE',
    TR2_SUB_CUSTOMER_REGISTRATION_ADDRESS_NOT_IN_NSW = 'TR2_SUB_CUSTOMER_REGISTRATION_ADDRESS_NOT_IN_NSW',
    TR2_SUB_LPN_NOMINATED_IS_NULL = 'TR2_SUB_LPN_NOMINATED_IS_NULL',
    TR2_SUB_INVALID_REQUEST = 'TR2_SUB_INVALID_REQUEST',
    TR2_SUB_INVALID_PARAMS = 'TR2_SUB_INVALID_PARAMS',
    TR2_SUB_TOLL_PROVIDER_EMAIL_NOT_MATCH = 'TR2_SUB_TOLL_PROVIDER_EMAIL_NOT_MATCH',
    TR2_SUB_PERSONAL_TOLL_ACCOUNT_HAS_ABN = 'TR2_SUB_PERSONAL_TOLL_ACCOUNT_HAS_ABN',
    TR2_SUB_ABN_ALREADY_LINKED = 'TR2_SUB_ABN_ALREADY_LINKED',
    TR2_SUB_ABR_BUSINESS_ADDRESS_NOT_NSW = 'TR2_SUB_ABR_BUSINESS_ADDRESS_NOT_NSW',
    TR2_SUB_ABN_SUPRESSED = 'TR2_SUB_ABN_SUPRESSED', // spelling error intentional
    TR2_SUB_ABR_ASSOCIATES_NOT_INCLUDES_APPLICANT = 'TR2_SUB_ABR_ASSOCIATES_NOT_INCLUDES_APPLICANT',
    TR2_SUB_ABR_NOT_ACTIVE_BEFORE_CURR_FIN_YEAR = 'TR2_SUB_ABR_NOT_ACTIVE_BEFORE_CURR_FIN_YEAR',
    TR2_SUB_ABR_ENTITY_NOT_ELIGIBLE = 'TR2_SUB_ABR_ENTITY_NOT_ELIGIBLE',
    TR2_SUB_ANZSIC_CODE_NOT_ELIGIBLE = 'TR2_SUB_ANZSIC_CODE_NOT_ELIGIBLE',
    TR2_SUB_TOLL_ACCOUNT_IS_NOT_ASSOCIATED_WITH_ABN = 'TR2_SUB_TOLL_ACCOUNT_IS_NOT_ASSOCIATED_WITH_ABN',
}

export enum OtherError {
    WORKFLOW_EXCEPTION = 'WORKFLOW_EXCEPTION',
    // note this error is specific to when an error occurs with the participant linking workflow
    SYNC_ERROR = 'SYNC_ERROR',
}

/** Different failures that /apply can return */
export type ApiApplyFailure = ApiEligibilityFailure | TR2_LINKING_ERROR | OtherError;

/** Converts an api eligibility failure code to a failure scenario */
function mapEligibilityReasonToScenario(failure: ApiEligibilityFailure): EligibilityFailureScenario {
    switch (failure) {
        case ApiEligibilityFailure.TR2_ABN_ABN_ALREADY_LINKED:
            return SoleTraderFailure.abnAlreadyLinked;

        case ApiEligibilityFailure.TR2_ABN_ABR_BUSINESS_ADDRESS_NOT_NSW:
            return SoleTraderFailure.businessNotInNSW;

        case ApiEligibilityFailure.TR2_ABN_ANZSIC_CODE_NOT_ELIGIBLE:
            return SoleTraderFailure.anzsicViolation;

        case ApiEligibilityFailure.TR2_ABN_ABN_SUPRESSED:
            return SoleTraderFailure.abnSuppressed;

        case ApiEligibilityFailure.TR2_ABN_ABR_ENTITY_NOT_ELIGIBLE:
            return SoleTraderFailure.abnNotSoleTrader;

        case ApiEligibilityFailure.TR2_ABN_ABR_ASSOCIATES_NOT_INCLUDES_APPLICANT:
            return SoleTraderFailure.unauthorised;

        case ApiEligibilityFailure.TR2_ABN_INACTIVE_ABN:
        case ApiEligibilityFailure.TR2_ABN_ABR_NOT_ACTIVE_BEFORE_CURR_FIN_YEAR:
            return SoleTraderFailure.abnInactive;

        case ApiEligibilityFailure.TOLL_ACCOUNT_ELIGIBILITY_TOLL_ACCOUNT_HAS_ABN:
        case ApiEligibilityFailure.TR2_TOLL_ACC_TOLL_ACCOUNT_IS_ASSOCIATED_WITH_ABN:
            return TollAccountFailure.tollAccountAssociatedToABN;

        case ApiEligibilityFailure.TOLL_ACCOUNT_ELIGIBILITY_TOLL_ACCOUNT_HAS_NO_ABN:
        case ApiEligibilityFailure.TR2_TOLL_ACC_TOLL_ACCOUNT_IS_NOT_ASSOCIATED_WITH_ABN:
            return TollAccountFailure.tollAccountNotAssociatedToABN;

        case ApiEligibilityFailure.DUPLICATE_TOLL_ACCOUNT:
        case ApiEligibilityFailure.TR2_TOLL_ACC_TOLL_ACCOUNT_NUMBER_ALREADY_LINKED:
            return TollAccountFailure.tollAccountAlreadyLinked;

        case ApiEligibilityFailure.TR2_TOLL_ACC_INELIGIBLE:
            return TollAccountFailure.tollAccountCannotBeVerified;

        case ApiEligibilityFailure.DUPLICATE_CUSTOMER:
        case ApiEligibilityFailure.TR2_TOLL_ACC_CUSTOMER_EXCEED_ALLOWED_LINKS:
            return TollAccountFailure.customerHasTollAccountLinkedToAnotherMyAccountId;

        case ApiEligibilityFailure.TOLL_ACCOUNT_ELIGIBILITY_TOLL_ACC_NOT_FOUND:
            return TollAccountFailure.tspApiTollAccountNotFound;

        case ApiEligibilityFailure.TR2_TOLL_ACC_TOLL_ACCOUNT_NUMBER_NOT_VALID:
        case ApiEligibilityFailure.TR2_TOLL_ACC_LPN_NOT_ASSOCIATED_WITH_ACC_NO:
        case ApiEligibilityFailure.TR2_TOLL_ACC_TOLL_ACCOUNT_NUMBER_VPN_NOT_EXIST:
        case ApiEligibilityFailure.TR2_TOLL_ACC_TOLL_PROVIDER_EMAIL_NOT_MATCH:
            return TollAccountFailure.tomeeTollAccountNotFound;

        case ApiEligibilityFailure.TOLL_ACCOUNT_ELIGIBILITY_TOLL_ACC_NOT_ACTIVE:
        case ApiEligibilityFailure.TR2_TOLL_ACC_TOLL_ACCOUNT_NUMBER_NOT_ACTIVE:
            return TollAccountFailure.tollAccountInactive;

        case ApiEligibilityFailure.TOLL_ACCOUNT_ELIGIBILITY_TOLL_ACCOUNT_PRODUCT_TYPE_INVALID:
            return TollAccountFailure.tollAccountProductTypeInvalid;

        case ApiEligibilityFailure.TOLL_ACCOUNT_ELIGIBILITY_TOLL_ACC_NOT_NSW:
            return TollAccountFailure.tollAccountOutsideNSW;

        case ApiEligibilityFailure.RESIDENTIAL_ADDRESS_WITHIN_STATE:
            return TollAccountFailure.myAccountOutsideNSW;

        case ApiEligibilityFailure.SERVICE_PROVIDER_ERROR:
            return TollAccountFailure.serviceProviderError
        case ApiEligibilityFailure.ETOLL_SERVICE_PROVIDER_NOT_AVAILABLE: //circuit breaker is open
            return TollAccountFailure.etollApiNotAvailable;
        case ApiEligibilityFailure.LINKT_SERVICE_PROVIDER_NOT_AVAILABLE: //circuit breaker is open
            return TollAccountFailure.linktApiNotAvailable;

        case ApiEligibilityFailure.FIELD_VALIDATION:
        case ApiEligibilityFailure.TR2_HAS_MIXED:
        case ApiEligibilityFailure.TR2_ABN_INVALID_PARAMS:
        case ApiEligibilityFailure.TR2_ABN_REQUIRED_ABN:
        case ApiEligibilityFailure.TR2_ABN_ABN_NOT_FOUND:
        case ApiEligibilityFailure.TR2_TOLL_ACC_INVALID_PARAMS:
        case ApiEligibilityFailure.TR2_TOLL_ACC_LPN_NOMINATED_IS_NULL:
        case ApiEligibilityFailure.TR2_TOLL_ACC_TOLL_ACCOUNT_TYPE_MISSING:
        case ApiEligibilityFailure.TR2_TOLL_ACC_TOLL_ACCOUNT_TYPE_INVALID:
            console.error('Received unexpected known ApiEligibilityFailure', failure);
            return 'other'; // Cases which we don't handle (some shouldn't occur without tampering)

        default:
            console.error('Received unexpected unknown ApiEligibilityFailure', failure);
            return 'other'; // Still handle it as the 'other' scenario, but with extra log for new relic
    }
}

export function mapApplyReasonToScenario(failure: ApiApplyFailure): EligibilityFailureScenario {
    if (failure in ApiEligibilityFailure) {
        return mapEligibilityReasonToScenario(failure as ApiEligibilityFailure);
    }

    switch (failure as TR2_LINKING_ERROR) {
        case TR2_LINKING_ERROR.TR2_SUB_TOLL_ACCOUNT_NUMBER_NOT_VALID:
        case TR2_LINKING_ERROR.TR2_SUB_LPN_NOT_ASSOCIATED_WITH_ACC_NO:
        case TR2_LINKING_ERROR.TR2_SUB_LPN_NOMINATED_IS_NULL:
        case TR2_LINKING_ERROR.TR2_SUB_TOLL_PROVIDER_EMAIL_NOT_MATCH: //only linkt
            return TollAccountFailure.tomeeTollAccountNotFound;

        case TR2_LINKING_ERROR.TR2_SUB_TOLL_ACCOUNT_NUMBER_ALREADY_LINKED:
            return TollAccountFailure.tollAccountAlreadyLinked;

        case TR2_LINKING_ERROR.TR2_SUB_TOLL_ACCOUNT_NUMBER_NOT_ACTIVE:
            return TollAccountFailure.tollAccountInactive;

        case TR2_LINKING_ERROR.TR2_SUB_CUSTOMER_REGISTRATION_ADDRESS_NOT_IN_NSW:
            return TollAccountFailure.myAccountOutsideNSW;

        case TR2_LINKING_ERROR.TR2_SUB_PERSONAL_TOLL_ACCOUNT_HAS_ABN:
            return TollAccountFailure.tollAccountAssociatedToABN;

        case TR2_LINKING_ERROR.TR2_SUB_ABR_BUSINESS_ADDRESS_NOT_NSW:
            return SoleTraderFailure.businessNotInNSW;

        case TR2_LINKING_ERROR.TR2_SUB_ABN_SUPRESSED:
            return SoleTraderFailure.abnSuppressed;

        case TR2_LINKING_ERROR.TR2_SUB_ABR_ASSOCIATES_NOT_INCLUDES_APPLICANT:
            return SoleTraderFailure.unauthorised;

        case TR2_LINKING_ERROR.TR2_SUB_ABR_NOT_ACTIVE_BEFORE_CURR_FIN_YEAR:
            return SoleTraderFailure.abnInactive;

        case TR2_LINKING_ERROR.TR2_SUB_ABR_ENTITY_NOT_ELIGIBLE:
            return SoleTraderFailure.abnNotSoleTrader;

        case TR2_LINKING_ERROR.TR2_SUB_ANZSIC_CODE_NOT_ELIGIBLE:
            return SoleTraderFailure.anzsicViolation;

        case TR2_LINKING_ERROR.TR2_SUB_TOLL_ACCOUNT_IS_NOT_ASSOCIATED_WITH_ABN:
            return TollAccountFailure.tollAccountNotAssociatedToABN;

        case TR2_LINKING_ERROR.TR2_SUB_ABN_ALREADY_LINKED:
            return SoleTraderFailure.abnAlreadyLinked;

        case TR2_LINKING_ERROR.TR2_SUB_CUSTOMER_EXCEED_ALLOWED_LINKS:
            return TollAccountFailure.customerHasTollAccountLinkedToAnotherMyAccountId;

        case TR2_LINKING_ERROR.TR2_SUB_INVALID_REQUEST:
        case TR2_LINKING_ERROR.TR2_SUB_INVALID_PARAMS:
            console.error('Received unexpected known ApiApplyFailure', failure);
            return 'other';

        default:
            console.error('Received unexpected unknown ApiApplyFailure', failure);
            return 'other';
    }
}

/**
 * Converts a list of eligibility api failures into failure scenarios we can action on the frontend.
 * Uses a set since many api claims could map to the same scenario.
 */
export function getEligibilityFailureScenarios(eligibilityReasons: ApiEligibilityFailure[]): Set<EligibilityFailureScenario> {
    return new Set(
        eligibilityReasons.map((reasonCode) => {
            return mapEligibilityReasonToScenario(reasonCode);
        }),
    );
}

export interface RegistrationResponse extends IBaseResponse {
    correlationId: string,
    customerReference: string,
    applicationStatus: string,
    applicationStatusReason?: string,
}

export interface Feature {
    readonly name: string;
    readonly fields: BaseFeatureFields;
}

export interface GetFeaturesResponse extends IBaseResponse {
    readonly features: readonly Feature[];
}
